import { Reducer } from "redux";

import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import {
    BAZAAR_GRID_LOAD_API_FAILURE,
    BAZAAR_GRID_LOAD_API_REQUEST,
    BAZAAR_GRID_LOAD_API_SUCCESS,
    BAZAAR_GRID_WATCHDOG_SUBSCRIBE,
    BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE,
    BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE,
    BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE,
    BazaarGridLoadApiFailureAction,
    BazaarGridLoadApiRequestAction,
    BazaarGridLoadApiSuccessAction,
    BazaarGridWatchdogSubscribeAction,
    BazaarGridWatchdogSubscribeApiFailureAction,
    BazaarGridWatchdogUnsubscribeAction,
    BazaarGridWatchdogUnsubscribeApiFailureAction,
} from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.actions";
import { hashFilter } from "@/mk2/apps/bazaar/helpers/filter";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

type BazaarGridActions =
    | BazaarGridLoadApiRequestAction
    | BazaarGridLoadApiSuccessAction
    | BazaarGridLoadApiFailureAction
    | BazaarGridWatchdogSubscribeAction
    | BazaarGridWatchdogSubscribeApiFailureAction
    | BazaarGridWatchdogUnsubscribeAction
    | BazaarGridWatchdogUnsubscribeApiFailureAction;

export interface PerFilterState {
    loadingState: LoadingState;
    errorMessage: string;
    facets: FacetsData;
    hits: number;
    hitsAll: number;
    hitsLiked: number;
    productsIds: number[];
    checked: {
        [productId: number]: true;
    };
    complaintsPerProductIds: {
        [productId: number]: number[];
    };
    watchdog: boolean;
    strollersIdsOrdered: number[];
    strollerPhotosIds: number[];
}

export const perFilterInitialState: PerFilterState = {
    loadingState: LoadingState.INIT,
    errorMessage: null,
    hits: null,
    hitsAll: null,
    hitsLiked: null,
    facets: null,
    productsIds: [],
    checked: {},
    complaintsPerProductIds: {},
    watchdog: false,
    strollersIdsOrdered: [],
    strollerPhotosIds: [],
};

const perFilterReducer: Reducer<PerFilterState> = (
    state: PerFilterState = perFilterInitialState,
    action: BazaarGridActions,
) => {
    switch (action.type) {
        case BAZAAR_GRID_LOAD_API_REQUEST:
        case BAZAAR_GRID_LOAD_API_FAILURE:
            return {
                ...state,
                loadingState: loadingState(state.loadingState, action),
                errorMessage: errorMessage(state.errorMessage, action),
            };
        case BAZAAR_GRID_LOAD_API_SUCCESS:
            return {
                ...state,
                loadingState: loadingState(state.loadingState, action),
                errorMessage: errorMessage(state.errorMessage, action),
                hits: action.hits,
                hitsAll: action.hitsAll,
                hitsLiked: action.hitsLiked,
                facets: action.facets,
                productsIds: action.response.result.products,
                complaintsPerProductIds: action.response.result.complaints,
                checked: action.checked,
                watchdog: action.watchdog,
                strollersIdsOrdered: action.strollersIdsOrdered,
                strollerPhotosIds: action.response.result.strollerPhotos,
            };
        case BAZAAR_GRID_WATCHDOG_SUBSCRIBE:
            return {
                ...state,
                watchdog: true,
            };
        case BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE:
            return {
                ...state,
                watchdog: false,
            };
        case BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE:
            // In case of failure - revert state
            return {
                ...state,
                watchdog: false,
            };
        case BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE:
            // In case of failure - revert state
            return {
                ...state,
                watchdog: true,
            };
        default:
            return state;
    }
};

export interface State {
    filters: {
        [filterHash: string]: PerFilterState;
    };
}

export const initialState: State = {
    filters: {},
};

const reducer: Reducer<State> = (state: State = initialState, action: BazaarGridActions) => {
    let hash;
    switch (action.type) {
        case BAZAAR_GRID_LOAD_API_REQUEST:
        case BAZAAR_GRID_LOAD_API_FAILURE:
        case BAZAAR_GRID_LOAD_API_SUCCESS:
        case BAZAAR_GRID_WATCHDOG_SUBSCRIBE:
        case BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE:
        case BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE:
        case BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE:
            hash = hashFilter(action.filter);
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [hash]: perFilterReducer(state.filters[hash], action),
                },
            };
        default:
            return state;
    }
};

export default reducer;
